import { AnyAction } from 'redux';
import { AxiosPromise, AxiosResponse } from 'axios';
import { ServerResponse } from 'data/types';
import { NodeData } from 'data/nodes/nodes.types';
import { SupportedFeaturesAccessFunctions, SupportedFeaturesCollectionFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';

export const API_PATH_XDPS = '/xdps';
export const API_PATH_GET_NETWORK_TOPOLOGY_FILTERS = '/network-topology-filters';
export const API_PATH_GET_NETWORK_TOPOLOGY_MOCKS = '/network-topology/mocks';
export const API_PATH_GET_NETWORK_TOPOLOGY_CONFIG = '/network-topology-config';

export const GET_NETWORK_TOPOLOGY = 'GET_NETWORK_TOPOLOGY';
export const GET_NETWORK_TOPOLOGY_PENDING = 'GET_NETWORK_TOPOLOGY_PENDING';
export const GET_NETWORK_TOPOLOGY_FULFILLED = 'GET_NETWORK_TOPOLOGY_FULFILLED';
export const GET_NETWORK_TOPOLOGY_REJECTED = 'GET_NETWORK_TOPOLOGY_REJECTED';
export const GET_NETWORK_TOPOLOGY_DELAYING = 'GET_NETWORK_TOPOLOGY_DELAYING';
export const FILTER_NETWORK_TOPOLOGY = 'FILTER_NETWORK_TOPOLOGY';

export const GET_NETWORK_TOPOLOGY_FILTERS = 'GET_NETWORK_TOPOLOGY_FILTERS';
export const GET_NETWORK_TOPOLOGY_FILTERS_PENDING = 'GET_NETWORK_TOPOLOGY_FILTERS_PENDNG';
export const GET_NETWORK_TOPOLOGY_FILTERS_FULFILLED = 'GET_NETWORK_TOPOLOGY_FILTERS_FULFILLED';
export const GET_NETWORK_TOPOLOGY_FILTERS_REJECTED = 'GET_NETWORK_TOPOLOGY_FILTERS_REJECTED';

export const SAVE_NETWORK_TOPOLOGY_FILTER = 'SAVE_NETWORK_TOPOLOGY_FILTER';
export const SET_NETWORK_TOPOLOGY_FILTER = 'SET_NETWORK_TOPOLOGY_FILTER';

export const LOAD_MOCK_DATA_FILE = 'LOAD_MOCK_DATA_FILE';
export const LOAD_MOCK_DATA_FILE_FULFILLED = 'LOAD_MOCK_DATA_FILE_FULFILLED';

export const GET_NETWORK_TOPOLOGY_CONFIG = 'GET_NETWORK_TOPOLOGY_CONFIG';
export const GET_NETWORK_TOPOLOGY_CONFIG_PENDING = 'GET_NETWORK_TOPOLOGY_CONFIG_PENDING';
export const GET_NETWORK_TOPOLOGY_CONFIG_FULFILLED = 'GET_NETWORK_TOPOLOGY_CONFIG_FULFILLED';
export const GET_NETWORK_TOPOLOGY_CONFIG_REJECTED = 'GET_NETWORK_TOPOLOGY_CONFIG_REJECTED';
export const GET_NETWORK_TOPOLOGY_CONFIG_DELAYING = 'GET_NETWORK_TOPOLOGY_CONFIG_DELAYING';
export const FILTER_NETWORK_TOPOLOGY_CONFIG = 'FILTER_NETWORK_TOPOLOGY_CONFIG';

export enum MOCK_DATA_ENUM {
  MOCK_1_AF_1_CF_1_XCIPIO,
  MOCK_10_AFS_10_CFS_1_CCPAG,
  MOCK_100_AFS_100_CFS_10_CCPAGS,
  MOCK_500_AFS_500_CFS_50_CCPAGS,
  MOCK_1000_AFS_1000_CFS_50_CCPAGS,
  MOCK_LINE_DIRECTIONS,
  MOCK_MARKER_COLLISIONS,
  MOCK_LINES,
  MOCK_LINK_POPUPS,
}

export enum NetworkTopologyElementsEnum {
  AccessFunction = 'af',
  CollectionFunction = 'cf',
  CCPAG = 'ccpag',
  CollectionFunctionGroup = 'cfGroup',
  Location = 'location',
  Xcipio = 'xcipio',
  MDF2 = 'mdf2',
  MDF3 = 'mdf3',
  ADMF = 'admf',
  SupportedFeature = 'supportedFeature',
  PoiXcipioNodeMap = 'poiXcipioNodeMap',
  CcpagMdf3Map = 'ccpagMdf3Map',
  CcpagConnectionDetails = 'ccpagConnectionDetails',
  ConfiguredAfs = 'configuredAfs'
}

export enum NetworkTopologyClusterElementsEnum {
  AccessFunctionCluster = 'afCluster',
  CollectionFunctionCluster = 'cfCluster',
  XcipioCluster = 'xcipioCluster',
  CCPAGCluster = 'ccpagCluster',
  MDF2Cluster = 'mdf2Cluster',
  MDF3Cluster = 'mdf3Cluster',
  ADMFCluster = 'admfCluster'
}

export enum NetworkTopologyStateEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  FAILED = 'FAILED',
  BLOCKED = 'BLOCKED',
  IDLE = 'IDLE',
  UNKNOWN ='UNKNOWN',
  LEADER = 'LEADER'
}

export interface NetworkTopologyLocationCity {
  id: string | number
  city:string,
  latitude: number,
  longitude: number,
  timezone: string,
  stateCode?: string,
}

export interface NetworkTopologyLocation {
  country: string,
  id: string | number,
  stateCode: string,
  stateName: string,
  cities: NetworkTopologyLocationCity[],
  citiesMap: Map<string, NetworkTopologyLocationCity>
}

export type NetworkTopologyElementsData = {
  [key in NetworkTopologyElementsEnum]: unknown[]
}

export type NetworkTopologyElement = {
  type: NetworkTopologyElementsEnum;
  location: {
      latitude: number | string;
      longitude: number | string;
  } | undefined;
  data: any;
  poiType: string,
  poiVendor: string,
  status: NetworkTopologyStateEnum,
  connections?: {[key: string]: Connection[]};
}

export interface NetworkTopology {
  facetLimit: number,
  mapPosition: { [key:string]: any },
  mapBaseLayer: { [key:string]: any }[],
  mapWMSBaseLayer: { [key:string]: any }[],
  data: NetworkTopologyElementsData,
}

export interface NetworkTopologyMapConfig {
  facetLimit: number,
  mapPosition: {
    zoom: number,
    maxZoom: number,
    center: [number, number]
  },
  mapBaseLayer: {
    [layerName: string]: {
      url: string,
      tileSize: number,
      zoomOffset: number
    }
  },
}

export type NetworkElementProps = {
  location: any;
  type: string,
  data: any;
  status: NetworkTopologyStateEnum;
  poiType?: string,
  poiVendor?: string,
  leafletId?: string,
  connections?: {[key: string]: Connection[]};
};

export type XcipioNodesTopology = {
  type: string,
  data: NodeData,
  location: { latitude: number, longitude: number },
  topologyData: { [key in NetworkTopologyElementsEnum]: NetworkElementProps[] }
}

export type NetworkTopologyMapData = {
  accessFunctions: any[],
  collectionFunctions: any[],
  xcipioNodes: any[],
  ccpags: any[],
  mdf2s: any[],
  mdf3s: any[],
  admfs: any[]
};

export type Connection = {
  connectionId?: string,
  destination: any,
  destinationId: any,
  status: NetworkTopologyStateEnum,
  sourceType?: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum,
  destType: NetworkTopologyElementsEnum | NetworkTopologyClusterElementsEnum,
  tooltipData?: any[]
};

export type NetworkTopologyUIState = {
  isLoading: boolean,
  isNetworkTopologyLoaded: boolean,
}

export type NetworkTopologyFilter = {
  search: string,
  location: {value: string, label: string }[],
  poiType: {value: string, label: string }[],
  poiVendor: {value: string, label: string }[],
  xcipioService: {value: string, label: string }[],
  cfType: {value: string, label: string }[],
  id?: string,
  name?: string,
  poiDataType?: string,
}

export interface NetworkTopologyFilterServerPayload {
  id?: string;
  filterJson: string;
}

export const newNetworkTopologyFilter: NetworkTopologyFilter = {
  search: '',
  location: [],
  poiType: [],
  poiVendor: [],
  xcipioService: [],
  cfType: [],
}

export interface NetworkTopologyState {
  original: XcipioNodesTopology[]
  entities: XcipioNodesTopology[]
  networkTopologyMapConfg: NetworkTopologyMapConfig
  ui: NetworkTopologyUIState
  customFilters: NetworkTopologyFilter[]
  selectedCustomFilter: NetworkTopologyFilter
  mapData: NetworkTopologyMapData
  originalMapData: NetworkTopologyMapData,
  collectionFunctions: CollectionFunction[],
  zoomInLocarion?: {
    latitude: number;
    longitude: number;
    zoom: number;
  }
}
export interface Mdf3Connection {
  id: string,
  NODENAME: string,
  NODETYPE: string,
  TOPOLOGY: string,
  IPADDR: string,
  OWNER: string,
  GROUP: string,
  ACCESS: string,
  IPADDR6: string,
  CITY: string,
  STATENAME: string,
  COUNTRY: string,
  LATITUDE: string,
  LONGITUDE: string,
  connectionStatuses: CCPAGMDF3ConnectionStatus[]
}

export interface CCPAGMDF3ConnectionStatus {
  CONNIP: string,
  CONNPORT: string,
  CONNSTATUS: string
}

export interface AfConnection {
  id: string,
  AFTYPE: string,
  AFPROTOCOL: string,
  IPADDR: string,
  CITY: string,
  STATENAME: string,
  COUNTRY: string,
  CONNSTATUS: string
}

export interface CcpagDetail {
  id: string,
  NODENAME: string,
  mdf3Connections: Mdf3Connection[],
  afConnections: AfConnection[]
}

export interface AdmfLeader {
  total: number,
  admfstatuslist: AdmfStatus[],
  islocaladmfleader: boolean
}

export interface AdmfStatus {
  endpoint: string
  nodename: string | null
  priority: number
  serverId: number
  status: string
  vip: string
}

export type XcipioNodeMOckDataEntry = {
  total: number,
  locations?: { latitude?: number, longitude?: number, country?:string, stateCode?: string, city?: string }[],
  afsMap?: { type: string, total: number }[],
  cfsMap?: { type: string, total: number }[],
  xcipioNodeMap?: { type: string, total: number }[]
}

export type PoiXcipioNodeMap = {
  id: number | string,
  poiId: string,
  liType: string,
  name: string,
  destNodeName: string
}

export interface GetNetworkTopologyAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY
  payload: {
    deliveryFunctionId: string,
    shouldUseNetworkTopologyMockData: boolean,
    shouldUseNetworkTopologyDinamicLocations: boolean,
  }
}

export interface GetNetworkTopologyPendingAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_PENDING
}
export interface GetNetworkTopologyFulfilledAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_FULFILLED
  payload: { element: NetworkTopologyElementsEnum; data: unknown[]; }[]
}

export interface GetNetworkTopologyRejectedAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_REJECTED
  payload: AxiosResponse<ServerResponse<NetworkTopology>>
}

export interface FilterNetworkTopologyAction extends AnyAction {
  type: typeof FILTER_NETWORK_TOPOLOGY
  payload: {
    filter: NetworkTopologyFilter,
    availableAccessFunction: SupportedFeaturesAccessFunctions,
    availableCollectionFunction: SupportedFeaturesCollectionFunctions,
  }
}

export interface GetNetworkTopologyFilterAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_FILTERS
}

export interface GetNetworkTopologyFilterPendingAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_FILTERS_PENDING
}

export interface GetNetworkTopologyFilterFulfilledAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_FILTERS_FULFILLED
  payload: AxiosResponse<ServerResponse<NetworkTopologyFilterServerPayload>>
}

export interface GetNetworkTopologyFilterRejectedAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_FILTERS_REJECTED
  payload: AxiosResponse<ServerResponse<void>>
}

export interface SetSelectedNetworkTopologyFilterAction extends AnyAction {
  type: typeof SET_NETWORK_TOPOLOGY_FILTER
  payload: NetworkTopologyFilter
}

export interface LoadMockDataFileAction extends AnyAction {
  type: typeof LOAD_MOCK_DATA_FILE
  payload: {element: NetworkTopologyElementsEnum; data: unknown[]; }[]
}

export interface LoadMockDataFileFulfilledAction extends AnyAction {
  type: typeof LOAD_MOCK_DATA_FILE_FULFILLED
  payload: any
}

export interface GetNetworkTopologyConfigAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_CONFIG
  payload: any
}

export interface GetNetworkTopologyConfigPendingAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_CONFIG_PENDING
}
export interface GetNetworkTopologyConfigFulfilledAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_CONFIG_FULFILLED
  payload: any
}

export interface GetNetworkTopologyConfigRejectedAction extends AnyAction {
  type: typeof GET_NETWORK_TOPOLOGY_CONFIG_REJECTED
  payload: AxiosResponse<ServerResponse<void>>
}

export type NetworkTopologyStateTypes =
  GetNetworkTopologyAction |
  GetNetworkTopologyPendingAction |
  GetNetworkTopologyFulfilledAction |
  GetNetworkTopologyRejectedAction |
  FilterNetworkTopologyAction |
  GetNetworkTopologyFilterAction |
  GetNetworkTopologyFilterPendingAction |
  GetNetworkTopologyFilterFulfilledAction |
  GetNetworkTopologyFilterRejectedAction |
  SetSelectedNetworkTopologyFilterAction |
  LoadMockDataFileAction |
  LoadMockDataFileFulfilledAction |
  GetNetworkTopologyConfigAction |
  GetNetworkTopologyConfigPendingAction |
  GetNetworkTopologyConfigFulfilledAction |
  GetNetworkTopologyConfigRejectedAction;
